import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import { useContext, useState, useEffect, useRef } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import { getServerURL } from '../utils';
import { ClinicRxContext } from '../App';
import { LOGOUT, UPDATE_ACCESS_TOKEN } from '../dispatchActionTypes';

const SERVER_URL = getServerURL();
const COUNTDOWN_DURATION = 59;
const ONE_SECOND = 1000;
const VISIBILITY_CHANGE = 'visibilitychange';

export default function ActivityDialog({ isOpen, handleClose, checkActivity }) {
  const { dispatch } = useContext(ClinicRxContext);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_DURATION);
  const intervalIdRef = useRef(null);

  const handleLogout = () => {
    clearInterval(intervalIdRef.current);
    fetch(`${SERVER_URL}/logout`, {
      credentials: 'include',
    }).catch((err) => console.error('Error making request to /logout:', err));
    dispatch({ type: LOGOUT });
    handleClose();
  };

  const updateAccessToken = (token) => {
    dispatch({
      type: UPDATE_ACCESS_TOKEN,
      payload: { accessToken: token },
    });
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      handleLogout();
    }
  };

  useEffect(() => {
    if (document.hidden) {
      handleLogout();
      return;
    }
    intervalIdRef.current = setInterval(() => {
      setSecondsLeft((curr) => curr - 1);
    }, ONE_SECOND);
    document.addEventListener(VISIBILITY_CHANGE, handleVisibilityChange);
    return () => {
      clearInterval(intervalIdRef.current);
      document.removeEventListener(VISIBILITY_CHANGE, handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      handleLogout();
    }
  }, [secondsLeft]);

  const handleDialogClose = (event, reason) => {
    if (reason) return;
    handleClose();
  };

  const handleClick = async () => {
    try {
      const refreshResult = await fetch(`${SERVER_URL}/refresh-token`, {
        credentials: 'include',
      });
      if (!refreshResult.ok) {
        setIsSnackbarOpen(true);
        handleLogout();
        return;
      }
      const { token } = await refreshResult.json();
      updateAccessToken(token);
      checkActivity();
    } catch (err) {
      console.error('Error in activity dialog request:', err);
      setIsSnackbarOpen(true);
      handleLogout();
    } finally {
      handleClose();
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleDialogClose}
        aria-labelledby='activity-dialog-title'
        aria-describedby='activity-dialog-description'
        role='activitydialog'
      >
        <DialogTitle id='activity-dialog-title'>
          Are you still working?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='activity-dialog-description'>
            Continue to keep your current session active.
          </DialogContentText>
          <DialogContentText
            component='p'
            sx={{
              mt: 1,
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
            aria-live='off'
          >
            0:{String(secondsLeft).padStart(2, '0')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleLogout();
            }}
          >
            Log Out
          </Button>
          <Button onClick={handleClick}>Continue</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isSnackbarOpen}
        onClose={() => setIsSnackbarOpen(false)}
        key={'topcenteractivitydialog'}
        autoHideDuration={7000}
      >
        <Alert severity='warning'>Something went wrong.</Alert>
      </Snackbar>
    </>
  );
}
